/**
 * A scenario type's bulk runs, or a dataset's runs, in a sheet beside the page, newest first and loading
 * more as the list is scrolled. A row says who started the run, when, in which mode and how it came out,
 * in the target's own counts; picking one opens it.
 */

import { useRef, type ReactNode } from 'react';
import { History, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useInfiniteScrollSentinel } from '@/hooks/use-infinite-scroll-sentinel';
import { ListRow } from '@/modules/simulator/conversations/conversation-parts';
import { useRunAuthor } from '@/modules/simulator/runs/use-run-author';
import { PanelState } from '@/modules/simulator/ui/panel-state';
import type { RunMode } from '@/types/websocket.types';
import { ModeBadge } from './run-detail';
import type { RunPages } from './use-run-pages';

/** What a row reads off a run, whatever it ran over. */
interface ListedRun {
	runId: string;
	userId: string;
	mode: RunMode;
	status: 'running' | 'done' | 'failed';
	startedAt: string;
}

interface RunsSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description: string;
	/** The target's list, mounted only while the sheet is open so each opening reads the runs afresh. */
	children: ReactNode;
}

export const RunsSheet = ({ open, onOpenChange, title, description, children }: RunsSheetProps) => (
	<Sheet open={open} onOpenChange={onOpenChange}>
		<SheetContent side='right' className='flex w-full flex-col gap-0 bg-background p-0 backdrop-blur-none sm:max-w-md'>
			<SheetHeader className='border-b border-gray-500 py-3 pr-12 pl-4'>
				<SheetTitle className='text-[15px] text-text-heading'>{title}</SheetTitle>
				<SheetDescription className='text-xs text-gray-800'>{description}</SheetDescription>
			</SheetHeader>
			{children}
		</SheetContent>
	</Sheet>
);

interface RunsListProps<T extends ListedRun> {
	pages: RunPages<T>;
	/** The run on the page behind the sheet; null when the page shows none. */
	currentRunId: string | null;
	/** How the run came out, in the target's own counts. */
	outcome: (run: T) => string;
	/** Said when the target has never run. */
	empty: string;
	onOpen: (run: T) => void;
}

export const RunsList = <T extends ListedRun>({ pages, currentRunId, outcome, empty, onOpen }: RunsListProps<T>) => {
	const { runs, isLoading, error, hasMore, loadingMore, loadMore } = pages;
	const authorOf = useRunAuthor();
	const listRef = useRef<HTMLDivElement | null>(null);
	const sentinelRef = useInfiniteScrollSentinel({
		hasNextPage: hasMore && !error,
		isFetchingNextPage: loadingMore,
		onLoadMore: loadMore,
		rootRef: listRef
	});

	return (
		<div ref={listRef} className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
			{isLoading ? (
				<PanelState icon={<Loader2 className='size-4 animate-spin' />}>Loading runs</PanelState>
			) : runs.length === 0 ? (
				<PanelState icon={<History className='size-5' />}>{error ?? empty}</PanelState>
			) : (
				<>
					{runs.map((run, index) => (
						<ListRow
							key={run.runId}
							title={authorOf(run.userId)}
							when={run.startedAt}
							preview={outcome(run)}
							meta={
								<span className='flex flex-wrap items-center gap-1 pt-1'>
									<ModeBadge mode={run.mode} />
									{/* Newest first, so the first row is the latest. */}
									{index === 0 && (
										<Badge variant='secondary' className='text-[10px]'>
											Latest
										</Badge>
									)}
									{run.status === 'failed' && (
										<Badge variant='destructive' className='text-[10px]'>
											Stopped
										</Badge>
									)}
								</span>
							}
							selected={run.runId === currentRunId}
							onSelect={() => onOpen(run)}
						/>
					))}
					{error && <p className='px-4 py-3 text-center text-xs text-red-700'>{error}</p>}
					{loadingMore && (
						<div className='flex justify-center py-3'>
							<Loader2 className='size-4 animate-spin text-gray-800' />
						</div>
					)}
					<div ref={sentinelRef} aria-hidden='true' className='h-px shrink-0' />
				</>
			)}
		</div>
	);
};
